import { LitElement, css, html } from 'lit-all'
import { StorefrontClient, t } from 'lib-utils'
import chunk from 'lodash-chunk'

class ASProductGrid extends LitElement {
  static properties = {
    collectionId: {
      type: String,
      attribute: 'collection-id',
    },
    collectionHandle: {
      type: String,
      attribute: 'collection-handle',
    },
    _layout: {
      type: String,
      attribute: 'layout',
      reflect: true
    },
    _products: { type: Array, state: true },
    _dimensions: { type: Object },
    _dimensionRange: { type: Number },
    _variantProperties: { type: Array },
    _currentPage: { type: Number, state: true },
    _perPage: { type: Number, state: true },
    _sortBy: { type: String, state: true },
    _filters: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
  }

  constructor() {
    super()
    this.apiUrl = 'https://flambeau-backend-xtjv.vercel.app' // 'http://localhost:3000'
    // this.apiUrl = 'http://localhost:3000'
    this._client = new StorefrontClient()
    this._loading = true
    this._section = document.querySelector('section[data-section-type="case-finder"]')

    this._products = []

    this._dimensions = {}
    this._variantProperties = ['length', 'width', 'depth', 'compartments']
    this._dimensionRange = 3

    this._layout = 'grid'
    this._sortBy = 'manual'
    this._perPage = 24
    this._currentPage = 1

    this._facetToPropertyMap = {
      'price.gte': 'price',
      'price.lte': 'price',
      'availability': 'availableForSale'
    }
  }

  get _sortedProducts() {
    return this._sortVariants(this._products, this._sortBy)
  }

  get _paginatedProducts() {
    return chunk(this._sortedProducts, this._perPage)
  }

  get _currentProducts() {
    const index = this._currentPage - 1
    return this._paginatedProducts[index]
  }

  async connectedCallback() {
    super.connectedCallback()

    await this._initProductGrid()


    document.addEventListener('case-finder:param-change', this._handleParamUpdates)

    document.addEventListener('case-finder:display-count', (e) => {
      console.log(e)
    })

  }

  disconnectedCallback() {
    document.removeEventListener('case-finider:param-change', this._handleParamUpdates)
  }

  // removes shadow dom so we can inherit the styles from theme.css 😬
  createRenderRoot() {
    return this
  }

  async _initProductGrid() {
    this._parseUrlParams()
    this._products = await this._fetchData()
    this._loading = false

    this._updateDisplayCount()
    this._updateFacetFilterCounts()
  }

  _parseUrlParams() {
    const params = new URLSearchParams(window.location.search)

    this._sortBy = params.get('sort_by') || 'manual'
    this._perPage = params.get('per_page') || '24'
    this._currentPage = parseInt(params.get('page')) || 1
    this._filters = this._buildFilters(params)

    // Dynamically add only those dimensions that exist in the URL parameters
    this._variantProperties.forEach((param) => {
      const value = params.get(param)
      if (value !== null) {
        this._dimensions[param] = Number(value)
      }
    })
  }

  _isCacheExpired(timestamp, expiryDuration) {
    const now = new Date().getTime()
    return now - timestamp > expiryDuration
  }

  async _fetchData() {
    try {
      const cacheKey = `products_${this.collectionId}`
      const expiryDuration = 30 * 60 * 1000 // 30 minutes in milliseconds
      const cachedData = sessionStorage.getItem(cacheKey)

      let products

      if (cachedData) {
        const { data, timestamp } = JSON.parse(cachedData)

        if (!this._isCacheExpired(timestamp, expiryDuration)) {
          products = data
        }
      }

      if (!products) {
        const data = await this._client.getProductsByCollectionId(this.collectionId)

        products = this._getProductVariants(data)

        // Store data along with the current timestamp
        sessionStorage.setItem(cacheKey, JSON.stringify({ data: products, timestamp: new Date().getTime() }))
      }

      const filteredProducts = this._filterVariantsByFacets(products)

      return this._filterVariantsByDimensions(filteredProducts)
    } catch (error) {
      console.error('Error fetching products:', error)
      this._error = 'Something went wrong'
      return []
    }
  }

  _buildFilters(params) {
    const paramsArray = Array.from(params.entries())
    const filterParams = paramsArray.filter(([key, value]) => key.includes('filter.v.') && value !== '')

    return filterParams.map(([key, value]) => ({
      facet: key.replace('filter.v.', ''),
      value: params.getAll(key).length > 1 ? params.getAll(key) : value
    }))
  }

  _handleParamUpdates = ({ detail }) => {
    const { param, value } = detail

    switch (param) {
      case 'sort_by':
        this._sortBy = value
        break
      case 'per_page':
        this._perPage = value
        break
    }

    this._initProductGrid()
  }

  _goToPage(page) {
    this._currentPage = page
    let url = new URL(window.location.href)
    url.searchParams.set('page', page)

    window.history.pushState({}, '', url)
    window.scrollTo({ top: 100, behavior: 'smooth', })

    this._updateDisplayCount()
  }

  _getProductVariants(products) {
    return products.map(product => product.variants).flat()
  }

  _filterVariantsByDimensions(variants) {
    return variants.filter(variant => {
      return Object.keys(this._dimensions).every(key => {
        const minValue = this._dimensions[key]
        const maxValue = minValue + this._dimensionRange
        const metafields = variant?.metafields

        if (!metafields) return false

        const dimensions = metafields.dimensions

        if (key === 'compartments' && !metafields.hasOwnProperty('as-flm')) return false

        const value = key === 'compartments'
          ? metafields['as-flm']['compartments']
          : dimensions && `inside_${key}_in` in dimensions
            ? dimensions[`inside_${key}_in`]
            : null

        if (!value) return false

        return key === 'compartments'
          ? value === minValue.toString()
          : minValue <= value && value <= maxValue
      })
    })
  }

  /**
  * Filters products based on dynamic criteria, supporting operations like 'gte', 'lte', and exact matches.
  * Utilizes a mapping to align filter facets with product properties when direct matches don't exist.
  *
  * @param {Array<Object>} products - Products to filter.
  * @returns {Array<Object>} Filtered products that match all criteria in `_filters`.
  *
  */
  _filterVariantsByFacets(variants) {
    // Helper function to evaluate if a product meets a filter criterion
    const meetsCriteria = (variant, filter) => {
      const [facet, operation] = filter.facet.split('.')
      const property = this._facetToPropertyMap[filter.facet] || facet
      const value = filter.value

      if (property in variant) {
        switch (operation) {
          case 'gte':
            return variant[property] >= value
          case 'lte':
            return variant[property] <= value
          default:
            return variant[property] == Number(value)
        }
      }

      return false
    }

    return variants.filter(variant => this._filters.every(filter => meetsCriteria(variant, filter)))
  }

  /**
  * Sorts products by criteria ('manual', 'best-selling', 'title', 'price', 'created') with optional order ('asc', 'desc').
  * 'manual' and 'best-selling' preserve original order. Others sort in specified or default ('asc') order.
  *
  * @param {Array} products - Array of products to sort.
  * @param {string} sortBy - Sort criteria and order ('criteria-order'). Defaults to 'asc' if order omitted.
  *                          'manual' and 'best-selling' ignore the order.
  * @returns {Array} Sorted products. Throws error for unsupported sort keys.
  */
  _sortVariants(variants, sortBy) {
    if (['manual', 'best-selling'].includes(sortBy)) return variants

    const sortProperties = sortBy.split('-')
    const value = sortProperties[0]
    const order = sortProperties[1] || 'ascending'

    const compare = (a, b) => {
      if (a < b) return order === 'ascending' ? -1 : 1
      if (a > b) return order === 'ascending' ? 1 : -1
      return 0
    }

    return [...variants].sort((a, b) => {
      switch (value) {
        case 'title':
          return compare(a.productTitle.toLowerCase(), b.productTitle.toLowerCase())
        case 'price':
          return compare(a.price, b.price)
        case 'created':
          return compare(a.createdAt, b.createdAt)
        default:
          throw new Error('Unsupported sort key')
      }
    })
  }

  _handleClear() {
    window.location = window.location.pathname
  }

  _updateFacetFilterCounts() {
    const filterGroups = document.querySelectorAll('.collection__filter-group')

    filterGroups.forEach(filter => {
      const button = filter.querySelector('.collection__filter-group-name')
      const labels = filter.querySelectorAll('label')
      const filterType = button.getAttribute('aria-controls').replace(/-/g, ".")
      const filterKey = filterType.replace('filter.v.', '')

      if (!labels.length) return

      const productProperty = this._facetToPropertyMap[filterKey]

      const numTrue = this._sortedProducts.filter(prod => prod[productProperty] === true).length
      const numFalse = this._sortedProducts.filter(prod => prod[productProperty] === false).length
      const trueLabel = Array.from(labels).find(label => label.getAttribute('for') === `${filterType}-1`)
      const falseLabel = Array.from(labels).find(label => label.getAttribute('for') === `${filterType}-0`)

      if (!trueLabel || !falseLabel) return
      trueLabel.innerHTML = trueLabel.innerHTML.replace(/\(\d+\)/, `(${numTrue})`)
      falseLabel.innerHTML = falseLabel.innerHTML.replace(/\(\d+\)/, `(${numFalse})`)
    })

  }

  _updateDisplayCount() {
    this.dispatchEvent(new CustomEvent('case-finder:display-count', {
      bubbles: true,
      composed: true,
      detail: {
        totalProducts: this._products.length,
        perPage: this._perPage,
        page: this._currentPage
      }
    }))
  }

  _renderLoadingState() {
    const loadingCards = Array.from({ length: 8 }, (v, i) => i)

    return html`
      <div class="product-list product-list--collection product-list--with-sidebar">
        ${loadingCards.map((v, i) => html`<as-skeleton-card class="1/3--tablet-and-up 1/4--desk"></as-skeleton-card>`)}
      </div>
    `
  }

  _renderEmptyState() {
    return html`
      <div class="empty-state empty-state--tight">
        <p class="empty-state__description">
          ${t('collection.general.no_results')}
        </p>

        <div class="empty-state__button-container">
          <button
            class="empty-state__button button button--primary"
            @click="${this._handleClear}"
          >
            ${t('collection.filter.clear_all')}
          </button>
        </div>
      </div>
    `
  }

  _renderPagination() {
    const totalPages = this._paginatedProducts.length

    return html`
      <div class="pagination">
        <div class="pagination__inner">
          ${this._currentPage > 1 ? html`
            <button class="pagination__prev link" @click="${() => this._goToPage(this._currentPage - 1)}">&larr; Previous</button>
          ` : ''}
          <div class="pagination__nav">
            ${Array.from({ length: totalPages }, (_, i) => i + 1).map(page => this._renderPaginationItem(page))}
          </div>
          <span class="pagination__page-count">Page ${this._currentPage} of ${totalPages}</span>
          ${this._currentPage < totalPages ? html`
            <button class="pagination__next link" @click="${() => this._goToPage(this._currentPage + 1)}">Next &rarr;</button>
          ` : ''}
        </div>
      </div>
    `
  }

  _renderPaginationItem(page) {
    return page === this._currentPage
      ? html`<span class="pagination__nav-item is-active">${page}</span>`
      : html`<button class="pagination__nav-item link" @click="${() => this._goToPage(page)}">${page}</button>`
  }

  render() {
    if (this._loading) return this._renderLoadingState()

    if (!this._sortedProducts.length) return this._renderEmptyState()

    return html`
      <div class="product-list product-list--collection product-list--with-sidebar">
        ${this._currentProducts.map(
      (item, i) =>
        html`<as-variant-card layout="${this._layout}" .variant="${item}" collection-handle="${this.collectionHandle}" />`
    )}
      </div>

      ${this._paginatedProducts.length > 1
        ? this._renderPagination()
        : html``
      }
    `
  }
}

customElements.define('as-product-grid', ASProductGrid)
